import Encoding from 'encoding-japanese';
import { ACCOUNT_TYPES, inferAccountType, isForProfit, isGeneralNonProfit } from '../constants';
import {
    BS_ASSET_TREE,
    BS_LIABILITY_TREE,
    BS_EQUITY_PROFIT,
    BS_EQUITY_NONPROFIT,
    NONOP_REVENUE_CODES,
    NONOP_EXPENSE_CODES,
    COGS_CODES,
} from './hot010Mapping';
import { getFiscalYearRange } from './date';

// --- CSV行生成ユーティリティ ---

function formatAmount(value) {
    if (value === 0 || value === null || value === undefined) return '';
    return String(Math.round(value));
}

function csvRow(name, amount, rowType, level, code, forceAmount = false) {
    let amt = '';
    if (amount !== null && amount !== undefined) {
        amt = forceAmount && amount === 0 ? '0' : formatAmount(amount);
    }
    return [name, amt, rowType, String(level), code].join(',');
}

// --- BSツリー走査 ---

function sumSystemCodes(systemCodes, accounts, getAccountBalance) {
    let total = 0;
    for (const code of systemCodes) {
        const acc = accounts.find(a => a.code === code);
        if (acc) total += getAccountBalance(acc.id);
    }
    return total;
}

function walkBsTree(nodes, accounts, getAccountBalance, rows) {
    let sectionTotal = 0;

    for (const node of nodes) {
        if (node.type === 'T') {
            // 子の合計が0なら親Tタイトル行ごと出力しない（空セクションのエラー回避）
            const childRows = [];
            const subtotal = node.children
                ? walkBsTree(node.children, accounts, getAccountBalance, childRows)
                : 0;
            if (subtotal !== 0) {
                rows.push(csvRow(node.name, null, 'T', node.level, node.code));
                rows.push(...childRows);
                sectionTotal += subtotal;
            }
        } else {
            // type:'1' 金額行
            if (node.systemCodes) {
                const amount = sumSystemCodes(node.systemCodes, accounts, getAccountBalance);
                if (amount !== 0) {
                    rows.push(csvRow(node.name, amount, '1', node.level, node.code));
                    sectionTotal += amount;
                }
            }
        }
    }

    return sectionTotal;
}

function generateBsRows(accounts, getAccountBalance, orgType) {
    const rows = [];

    // 資産の部
    const totalAssets = walkBsTree(BS_ASSET_TREE, accounts, getAccountBalance, rows);
    rows.push(csvRow('資産合計', totalAssets, '1', 2, '10A000020'));

    // 負債の部
    const totalLiabilities = walkBsTree(BS_LIABILITY_TREE, accounts, getAccountBalance, rows);
    rows.push(csvRow('負債合計', totalLiabilities, '1', 2, '10B000020'));

    // 純資産/正味財産の部
    const equityTree = isGeneralNonProfit(orgType) ? BS_EQUITY_NONPROFIT : BS_EQUITY_PROFIT;
    const rowsBeforeEquity = rows.length;
    const totalEquity = walkBsTree(equityTree, accounts, getAccountBalance, rows);

    // 当期純利益/正味財産増減額を純資産に加算
    const totalRevenue = accounts
        .filter(a => inferAccountType(a) === ACCOUNT_TYPES.REVENUE)
        .reduce((sum, a) => sum + getAccountBalance(a.id), 0);
    const totalExpense = accounts
        .filter(a => inferAccountType(a) === ACCOUNT_TYPES.EXPENSE)
        .reduce((sum, a) => sum + getAccountBalance(a.id), 0);
    const netIncome = totalRevenue - totalExpense;

    // 当期純利益/正味財産増減額: コード表外科目なので親T行（10C000010）の枝番で記録
    // walkBsTreeが部タイトルを出力しなかった場合（配下科目が全ゼロ）は手動で追加
    const equityLabel = isGeneralNonProfit(orgType) ? '当期正味財産増減額' : '当期純利益';
    if (netIncome !== 0) {
        const equitySectionLabel = isGeneralNonProfit(orgType) ? '正味財産の部' : '純資産の部';
        if (rows.length === rowsBeforeEquity) {
            rows.push(csvRow(equitySectionLabel, null, 'T', 2, '10C000010'));
        }
        rows.push(csvRow(equityLabel, netIncome, '1', 3, '10C000010-1'));
    }

    const totalEquityFinal = totalEquity + netIncome;
    const equitySumLabel = isGeneralNonProfit(orgType) ? '正味財産合計' : '純資産合計';
    rows.push(csvRow(equitySumLabel, totalEquityFinal, '1', 2, '10C000030'));

    rows.push(csvRow('負債純資産合計', totalLiabilities + totalEquityFinal, '1', 2, '10C000040'));

    return rows;
}

// --- PLツリー生成 ---

function generatePlRows(accounts, getAccountBalance, orgType) {
    // PLコード体系（e-Tax公式コード表 一般商工業）
    // 10D=収益 / 10E=費用 / 10F=損益計算
    const rows = [];
    const isNonprofit = isGeneralNonProfit(orgType);

    const revenueAccounts = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.REVENUE);
    const expenseAccounts = accounts.filter(a => inferAccountType(a) === ACCOUNT_TYPES.EXPENSE);

    // 売上高/経常収益: T(10D100010 lv2) → T(10D100020 lv3) → 合計(10D100030 lv4)
    const opRevAccounts = revenueAccounts.filter(a =>
        !NONOP_REVENUE_CODES.has(a.code) && a.plSection !== 'extraordinary'
    );
    const opRevTotal = opRevAccounts.reduce((sum, a) => sum + getAccountBalance(a.id), 0);
    rows.push(csvRow(isNonprofit ? '経常収益' : '営業活動による収益', null, 'T', 2, '10D100010'));
    rows.push(csvRow(isNonprofit ? '経常収益' : '売上高', null, 'T', 3, '10D100020'));
    rows.push(csvRow(isNonprofit ? '経常収益合計' : '売上高合計', opRevTotal, '1', 4, '10D100030', true));

    // 売上原価: T(10E100010 lv2) → T(10E100020 lv3) → 合計(10E100030 lv4) → 売上総利益(10F000010 lv2)
    const cogsAccounts = expenseAccounts.filter(a => COGS_CODES.has(a.code));
    const cogsTotal = cogsAccounts.reduce((sum, a) => sum + getAccountBalance(a.id), 0);
    if (cogsTotal !== 0) {
        rows.push(csvRow('売上原価', null, 'T', 2, '10E100010'));
        rows.push(csvRow('売上原価', null, 'T', 3, '10E100020'));
        rows.push(csvRow('売上原価合計', cogsTotal, '1', 4, '10E100030'));
        rows.push(csvRow('売上総利益', opRevTotal - cogsTotal, '1', 2, '10F000010'));
    }

    // 販管費/経常費用: T(10E200010 lv2) → 合計(10E201330 lv3)
    const sgaAccounts = expenseAccounts.filter(a =>
        !COGS_CODES.has(a.code) &&
        !NONOP_EXPENSE_CODES.has(a.code) &&
        a.plSection !== 'extraordinary' &&
        a.plSection !== 'designated'
    );
    const sgaTotal = sgaAccounts.reduce((sum, a) => sum + getAccountBalance(a.id), 0);
    rows.push(csvRow(isNonprofit ? '経常費用' : '販売費及び一般管理費', null, 'T', 2, '10E200010'));
    rows.push(csvRow(isNonprofit ? '経常費用合計' : '販売費及び一般管理費合計', sgaTotal, '1', 3, '10E201330', true));

    // 営業利益/経常増減額: 10F000110 lv2
    const operatingIncome = opRevTotal - cogsTotal - sgaTotal;
    rows.push(csvRow(isNonprofit ? '経常増減額' : '営業利益', operatingIncome, '1', 2, '10F000110', true));

    // 営業外収益: T(10D200010 lv2) → 合計(10D200740 lv3)
    const nonopRevAccounts = revenueAccounts.filter(a => NONOP_REVENUE_CODES.has(a.code));
    const nonopRevTotal = nonopRevAccounts.reduce((sum, a) => sum + getAccountBalance(a.id), 0);
    if (nonopRevTotal !== 0) {
        rows.push(csvRow('営業外収益', null, 'T', 2, '10D200010'));
        rows.push(csvRow('営業外収益合計', nonopRevTotal, '1', 3, '10D200740'));
    }

    // 営業外費用: T(10E300010 lv2) → 合計(10E300910 lv3)
    const nonopExpAccounts = expenseAccounts.filter(a => NONOP_EXPENSE_CODES.has(a.code));
    const nonopExpTotal = nonopExpAccounts.reduce((sum, a) => sum + getAccountBalance(a.id), 0);
    if (nonopExpTotal !== 0) {
        rows.push(csvRow('営業外費用', null, 'T', 2, '10E300010'));
        rows.push(csvRow('営業外費用合計', nonopExpTotal, '1', 3, '10E300910'));
    }

    // 経常利益: 10F000130 lv2
    const ordinaryIncome = operatingIncome + nonopRevTotal - nonopExpTotal;
    rows.push(csvRow('経常利益', ordinaryIncome, '1', 2, '10F000130', true));

    // 特別利益: T(10D300010 lv2) → 合計(10D300670 lv3)
    const extraRevAccounts = revenueAccounts.filter(a => a.plSection === 'extraordinary');
    const extraRevTotal = extraRevAccounts.reduce((sum, a) => sum + getAccountBalance(a.id), 0);
    if (extraRevTotal !== 0) {
        rows.push(csvRow('特別利益', null, 'T', 2, '10D300010'));
        rows.push(csvRow('特別利益合計', extraRevTotal, '1', 3, '10D300670'));
    }

    // 特別損失: T(10E400010 lv2) → 合計(10E401090 lv3)
    const extraExpAccounts = expenseAccounts.filter(a => a.plSection === 'extraordinary');
    const extraExpTotal = extraExpAccounts.reduce((sum, a) => sum + getAccountBalance(a.id), 0);
    if (extraExpTotal !== 0) {
        rows.push(csvRow('特別損失', null, 'T', 2, '10E400010'));
        rows.push(csvRow('特別損失合計', extraExpTotal, '1', 3, '10E401090'));
    }

    // 税引前当期純利益: 10F000150 lv2
    const preTaxIncome = ordinaryIncome + extraRevTotal - extraExpTotal;
    rows.push(csvRow('税引前当期純利益', preTaxIncome, '1', 2, '10F000150', true));

    return rows;
}

// --- CSVファイル生成 ---

function buildCsvContent(statementType, statementName, orgName, fiscalYear, dataRows) {
    const { start, end } = getFiscalYearRange(fiscalYear);
    const startStr = String(start).slice(0, 10);
    const endStr = String(end).slice(0, 10);

    const headerRows = [
        `A,${statementType},,,`,
        `B,${orgName},,,`,
        `C1,${startStr},,,`,
        `C2,${endStr},,,`,
        `${statementName},,,,`,
    ];

    return [...headerRows, ...dataRows].join('\r\n') + '\r\n';
}

function downloadShiftJisCsv(content, filename) {
    const unicodeArray = Encoding.stringToCode(content);
    const sjisArray = Encoding.convert(unicodeArray, { to: 'SJIS', from: 'UNICODE' });
    const uint8Array = new Uint8Array(sjisArray);
    const blob = new Blob([uint8Array], { type: 'text/csv;charset=Shift_JIS' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

// --- メインエクスポート関数 ---

export function generateHOT010Content({ orgName, fiscalYear, accounts, getAccountBalance, orgType }) {
    const bsDataRows = generateBsRows(accounts, getAccountBalance, orgType);
    const bsContent = buildCsvContent('BS', '貸借対照表', orgName, fiscalYear, bsDataRows);
    const plDataRows = generatePlRows(accounts, getAccountBalance, orgType);
    const plContent = buildCsvContent('PL', '損益計算書', orgName, fiscalYear, plDataRows);
    return { bsContent, plContent };
}

export function exportHOT010({ orgName, fiscalYear, accounts, getAccountBalance, orgType }) {
    const { bsContent, plContent } = generateHOT010Content({ orgName, fiscalYear, accounts, getAccountBalance, orgType });
    downloadShiftJisCsv(bsContent, `HOT010_3.0_BS.csv`);
    setTimeout(() => downloadShiftJisCsv(plContent, `HOT010_3.0_PL.csv`), 500);
}
